import React, { useState, useCallback } from 'react';
import { extractVideoId, createEmbedUrl, handleIframeError } from '../utils/youtubeUtils';

const YouTubeVideo = ({ 
  videoUrl, 
  title = "YouTube Video", 
  className = "", 
  style = {},
  onError = null,
  fallbackMessage = null 
}) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const videoId = extractVideoId(videoUrl);
  const embedUrl = videoId ? createEmbedUrl(videoId) : null;

  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleIframeError = useCallback((event) => {
    setIsLoading(false);
    setHasError(true);
    
    if (onError) {
      onError(event);
    } else {
      // Default error handling
      handleIframeError(event, videoId, title);
    }
  }, [onError, videoId, title]);

  if (!embedUrl) {
    return (
      <div className={`flex items-center justify-center bg-gray-900 text-white p-8 ${className}`} style={style}>
        <div className="text-center">
          <div className="text-red-500 mb-2">⚠️</div>
          <p>Invalid YouTube URL</p>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className={`flex items-center justify-center bg-gray-900 text-white p-8 ${className}`} style={style}>
        <div className="text-center">
          <div className="mb-4">
            <svg className="w-16 h-16 mx-auto text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
            </svg>
          </div>
          <h3 className="text-lg font-semibold mb-2">Video Unavailable</h3>
          <p className="text-gray-300 mb-4">
            {fallbackMessage || "This video cannot be displayed due to embedding restrictions."}
          </p>
          <a 
            href={`https://www.youtube.com/watch?v=${videoId}`}
            target="_blank" 
            rel="noopener noreferrer"
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors inline-block"
          >
            Watch on YouTube
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} style={style}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900 text-white">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
            <p>Loading video...</p>
          </div>
        </div>
      )}
      <iframe
        src={embedUrl}
        title={title}
        className="w-full h-full"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
        allowFullScreen
        onLoad={handleIframeLoad}
        onError={handleIframeError}
        style={{ opacity: isLoading ? 0 : 1, transition: 'opacity 0.3s ease' }}
      />
    </div>
  );
};

export default YouTubeVideo;
